import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomSelect from "../../../../../components/Select/Select";
import { assignFormByAppointmentIdWoLoader } from "../../../../../redux/reducers/clinicSlice";
import {
  createPrescriptionWoLoader,
  createProcedureWoLoader,
  getPetsCompliantSummaryWoLoader,
  updatePrescriptionByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { showSnackBar } from "../../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../../util/AppColors";
import { getProcedureDetailsNew } from "../../../../../util/function";

const defObj = {
  clinic: {},
  vets: [],
  services: [],
  stocks: [],
  facilities: [],
  locations: [],
  serviceRemainders: [],
  forms: [],
};

const initVal = {
  name: null,
  service: null,
  serviceId: null,
  variant: null,
  variantId: null,
  quantity: 1,
  doseInSeries: null,
  servErr: false,
  varErr: false,
  qtyErr: false,
  doseErr: false,
};

const reqTxt = "This field is required";

const getRemainderId = (tmp) =>
  tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;

const getTemplateLinkedIds = (tmp) =>
  (tmp?.linkedServices ?? tmp?.serviceIds ?? [])
    ?.map((ser) =>
      typeof ser === "object" && ser !== null
        ? (ser?.serviceId ?? ser?.value)
        : ser,
    )
    ?.filter((id) => id != null && id !== "");

const templateLinksService = (tmp, serviceId) => {
  if (serviceId == null || serviceId === "") return false;
  return getTemplateLinkedIds(tmp)?.some(
    (id) => String(id) === String(serviceId),
  );
};

const getRemainderSteps = (tmp) => {
  if (Array.isArray(tmp?.steps) && tmp.steps.length) return tmp.steps;
  if (
    Array.isArray(tmp?.serviceRemainderSteps) &&
    tmp.serviceRemainderSteps.length
  ) {
    return tmp.serviceRemainderSteps;
  }
  return [];
};

const parseDueAfterDays = (st) => {
  const n = Number(st?.dueAfterDays ?? st?.due_after_days ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const getDoseInSeriesOptions = (remainders, serviceId) => {
  if (serviceId == null || serviceId === "") return [];
  const linkedTemps = (Array.isArray(remainders) ? remainders : []).filter(
    (tmp) => templateLinksService(tmp, serviceId),
  );
  const prefixTemplate = linkedTemps.length > 1;
  const options = [];
  linkedTemps.forEach((tmp) => {
    const steps = getRemainderSteps(tmp);
    if (!steps.length) return;
    const occurrence = tmp?.occurrenceLabel?.trim() || "Dose";
    const remainderId = getRemainderId(tmp);
    const templateName = tmp?.templateName ?? tmp?.name ?? "";
    steps.forEach((st, index) => {
      const stepName = st?.stepName ?? st?.name ?? `${occurrence} ${index + 1}`;
      const stepId = st?.stepId ?? st?.id ?? `${remainderId}-${index + 1}`;
      const displayName =
        prefixTemplate && templateName
          ? `${templateName} · ${stepName}`
          : stepName;
      options.push({
        name: displayName,
        value: displayName,
        stepId,
        stepName,
        stepOrder: Number(st?.stepOrder ?? st?.order ?? index + 1),
        dueAfterDays: parseDueAfterDays(st),
        serviceRemainderId: remainderId,
        templateName,
      });
    });
  });
  return options;
};

const getOccurrenceLabel = (remainders, serviceId) => {
  if (serviceId == null || serviceId === "") return "Dose";
  const tmp = (Array.isArray(remainders) ? remainders : []).find((t) =>
    templateLinksService(t, serviceId),
  );
  return tmp?.occurrenceLabel?.trim() || "Dose";
};

const getFormId = (frm) => frm?.formId ?? frm?.id ?? null;

const AddProcedureDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initVal);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const clinicForms = useSelector((state) => state?.clinic?.forms);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    clinic: clinicDet,
    services: clinicServices,
    stocks: clinicStockList,
    facilities,
    locations,
    serviceRemainders,
    forms: mandateForms,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        services: Array.isArray(reqData?.services) ? reqData?.services : [],
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        serviceRemainders: Array.isArray(reqData?.serviceRemainders)
          ? reqData.serviceRemainders
          : [],
        forms: Array.isArray(reqData?.forms) ? reqData.forms : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  const allForms = useMemo(() => {
    if (Array.isArray(mandateForms) && mandateForms.length > 0) {
      return mandateForms;
    }
    return Array.isArray(clinicForms) ? clinicForms : [];
  }, [mandateForms, clinicForms]);

  const services = clinicServices ?? [];
  const clinicStocks = clinicStockList ?? [];

  useEffect(() => {
    if (!open) return;
    setValues(initVal);
    setSelectedItems([]);
    setIsSaving(false);
    savingRef.current = false;
  }, [open]);

  const onApiCall = () => {
    if (appDet?.appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    }
  };

  const getValidationErrors = (item) => {
    const serviceId = item?.serviceId ?? item?.service?.serviceId;
    const doseRequired =
      getDoseInSeriesOptions(serviceRemainders, serviceId).length > 0;
    const errors = {};
    if (!serviceId) errors.servErr = true;
    if (!(item?.variantId ?? item?.variant?.variantId)) errors.varErr = true;
    if (!item?.quantity || Number(item?.quantity) === 0) errors.qtyErr = true;
    if (doseRequired && !item?.doseInSeries?.stepId) errors.doseErr = true;
    return errors;
  };

  const createProcedureItem = async (prcd) => {
    try {
      const serviceId = prcd?.serviceId ?? prcd?.service?.serviceId;
      const variantId = prcd?.variantId ?? prcd?.variant?.variantId;
      if (!serviceId || !variantId) return false;

      const reqQty = Number(prcd?.quantity) > 0 ? Number(prcd?.quantity) : 1;

      let procedureDetails = [];
      let kitPrescriptions = [];
      try {
        const procedureDetailsResult = await getProcedureDetailsNew({
          servId: serviceId,
          varntId: variantId,
          services: services ?? [],
          clinicStocks: clinicStocks ?? [],
          pharmacyTaxId: clinicDet?.pharmacyTaxId,
          status: true,
          facilities,
          facilityId: appDet?.facilityId,
          patientWorkbook: true,
          locations: locations ?? [],
          clinicId: clinicDet?.id,
        });
        procedureDetails = Array.isArray(
          procedureDetailsResult?.procedureDetails,
        )
          ? procedureDetailsResult.procedureDetails
          : [];
        kitPrescriptions = Array.isArray(procedureDetailsResult?.prescriptions)
          ? procedureDetailsResult.prescriptions
          : [];
      } catch {
        procedureDetails = [];
        kitPrescriptions = [];
      }

      // Kit prescriptions tied to this procedure only (not extra procedures)
      if (kitPrescriptions.length > 0) {
        const data = {
          status: "Pending",
          remark: "",
          prescription: kitPrescriptions,
          issue: "",
          paymentId: reqDet?.paymentId ?? null,
        };
        if (reqDet?.prescription?.[0]?.prescriptionId) {
          await dispatch(
            updatePrescriptionByIdWoLoader({
              prescriptionId: reqDet?.prescription?.[0]?.prescriptionId,
              data,
            }),
          );
        } else if (appDet?.appointmentId) {
          await dispatch(
            createPrescriptionWoLoader({
              appointmentId: appDet?.appointmentId,
              data,
            }),
          );
        }
      }

      const resServ =
        (services ?? []).find((ser) => ser?.serviceId === serviceId) ?? null;
      const reqVar =
        resServ?.serviceVariants?.find(
          (vnt) =>
            String(vnt?.variantId) === String(variantId) ||
            vnt?.variantName === prcd?.variant?.name,
        ) ?? null;

      let tax = 0;
      let total = reqVar?.servicePrice ?? prcd?.variant?.servicePrice ?? 0;
      if ((resServ?.taxApplicability ?? "").toLowerCase() === "yes") {
        tax =
          (Number(reqVar?.servicePrice ?? 0) / 100) * Number(resServ?.tax ?? 0);
        total += tax;
      }

      const data = {
        procedureName: prcd?.service?.name,
        serviceId,
        category: prcd?.service?.category,
        subCategory: prcd?.service?.subCategory,
        remarks: prcd?.service?.remarks,
        doneAt: moment(new Date()).format("YYYY-MM-DD"),
        status: "Pending",
        petId: appDet?.petId,
        appointmentId: appDet?.appointmentId,
        serviceCharge: prcd?.variant?.servicePrice ?? reqVar?.servicePrice ?? 0,
        serviceType: prcd?.variant?.name,
        procedureDetails,
        tax: Number(Number(tax) * Number(reqQty)) || 0,
        total: Number(Number(total) * Number(reqQty)) || 0,
        mrpPrice: Number(
          reqVar?.servicePrice ?? prcd?.variant?.servicePrice ?? 0,
        ),
        procedureVariantId: variantId,
        quantity: Number(reqQty),
        patientWorkbook: true,
        mainProcedure: true,
        hsnSacCode: resServ?.hsnSacCode ?? null,
        paymentId: reqDet?.paymentId ?? null,
        ...(prcd?.doseInSeries?.stepId
          ? {
              doseInSeries: prcd?.doseInSeries?.stepId,
              serviceRemainderId:
                prcd?.doseInSeries?.serviceRemainderId ?? null,
            }
          : {}),
      };

      const res = await dispatch(
        createProcedureWoLoader({ data, successMessage: "" }),
      );
      return !!res?.payload;
    } catch {
      return false;
    }
  };

  const assignLinkedFormsForServiceIds = async (serviceIds = []) => {
    const apntId = appDet?.appointmentId;
    if (!apntId || !serviceIds?.length) return;

    const assignedFormIds = new Set(
      (reqDet?.forms ?? [])
        ?.map((frm) => getFormId(frm))
        ?.filter(Boolean)
        ?.map(String),
    );

    const formIdsToAssign = new Set();
    serviceIds.forEach((serviceId) => {
      (allForms ?? []).forEach((frm) => {
        if (!templateLinksService(frm, serviceId)) return;
        const formId = getFormId(frm);
        if (!formId || assignedFormIds.has(String(formId))) return;
        formIdsToAssign.add(formId);
      });
    });

    for (const formId of formIdsToAssign) {
      await dispatch(
        assignFormByAppointmentIdWoLoader({
          apntId,
          formId,
        }),
      );
    }
  };

  const handleAdd = () => {
    const errors = getValidationErrors(values);
    if (Object.keys(errors).length > 0) {
      setValues((prev) => ({ ...prev, ...errors }));
      return;
    }

    const serviceId = values?.serviceId ?? values?.service?.serviceId;
    const alreadyQueued = selectedItems.some(
      (item) =>
        String(item?.serviceId ?? item?.service?.serviceId) ===
        String(serviceId),
    );
    if (alreadyQueued) {
      dispatch(
        showSnackBar({
          message: "This procedure is already added",
          type: "error",
        }),
      );
      return;
    }

    setSelectedItems((prev) => [
      ...prev,
      {
        ...values,
        servErr: false,
        varErr: false,
        qtyErr: false,
        doseErr: false,
      },
    ]);
    setValues(initVal);
    dispatch(
      showSnackBar({
        message: "Item added successfully",
        type: "success",
      }),
    );
  };

  const handleDone = async () => {
    if (savingRef.current || isSaving) return;

    const currentServiceId =
      values?.serviceId ?? values?.service?.serviceId ?? null;
    const hasCurrentItem = !!currentServiceId;

    let itemsToSave = [...(selectedItems ?? [])];

    if (hasCurrentItem) {
      const errors = getValidationErrors(values);
      if (Object.keys(errors).length > 0) {
        setValues((prev) => ({ ...prev, ...errors }));
        return;
      }

      const alreadyQueued = itemsToSave.some(
        (item) =>
          String(item?.serviceId ?? item?.service?.serviceId) ===
          String(currentServiceId),
      );
      if (!alreadyQueued) {
        itemsToSave = [
          ...itemsToSave,
          {
            ...values,
            servErr: false,
            varErr: false,
            qtyErr: false,
            doseErr: false,
          },
        ];
      }
    }
    // No PROCEDURE / SERVICE selected — close without validating

    // Only explicitly added items — drop anything incomplete
    itemsToSave = itemsToSave.filter((item) => {
      const errs = getValidationErrors(item);
      return Object.keys(errs).length === 0;
    });

    if (itemsToSave.length === 0) {
      onClose();
      return;
    }

    savingRef.current = true;
    setIsSaving(true);

    let successCount = 0;
    const savedServiceIds = [];
    for (let i = 0; i < itemsToSave.length; i += 1) {
      const ok = await createProcedureItem(itemsToSave[i]);
      if (ok) {
        successCount += 1;
        const serviceId =
          itemsToSave[i]?.serviceId ?? itemsToSave[i]?.service?.serviceId;
        if (serviceId != null && serviceId !== "") {
          savedServiceIds.push(serviceId);
        }
      }
    }

    if (successCount > 0) {
      await assignLinkedFormsForServiceIds(savedServiceIds);
    }

    savingRef.current = false;
    setIsSaving(false);

    if (successCount > 0) {
      dispatch(
        showSnackBar({
          message:
            successCount > 1
              ? "Procedures added successfully"
              : "Procedure added successfully",
          type: "success",
        }),
      );
      onApiCall();
      setSelectedItems([]);
      setValues(initVal);
      onClose();
    } else {
      dispatch(
        showSnackBar({
          message: "Unable to add procedure. Please try again.",
          type: "error",
        }),
      );
    }
  };

  const getServiceList = () => {
    const addedServiceIds = new Set(
      (Array.isArray(reqDet?.procedures) ? reqDet.procedures : [])
        ?.map((prcd) => prcd?.serviceId)
        ?.filter((id) => id != null && id !== "")
        ?.map((id) => String(id)),
    );
    (selectedItems ?? []).forEach((item) => {
      const id = item?.serviceId ?? item?.service?.serviceId;
      if (id != null && id !== "") addedServiceIds.add(String(id));
    });

    return (services ?? [])
      ?.filter(
        (sl) =>
          sl?.active === "Y" &&
          !["lab", "radiology"].includes((sl?.category ?? "")?.toLowerCase()) &&
          !addedServiceIds.has(String(sl?.serviceId)),
      )
      ?.map((ser) => ({
        name: ser?.serviceName,
        value: ser?.serviceName,
        serviceId: ser?.serviceId,
        category: ser?.category,
        subCategory: ser?.subCategory,
        remarks: ser?.description,
      }));
  };

  const onChange = (name, value) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "service") {
        reqObj.serviceId = value?.serviceId ?? null;
        reqObj.servErr = value?.serviceId ? false : true;
        reqObj.variant = null;
        reqObj.variantId = null;
        reqObj.varErr = true;
        reqObj.doseInSeries = null;
        reqObj.doseErr = false;
      }
      if (name === "variant") {
        reqObj.variantId = value?.variantId ?? null;
        reqObj.varErr = value?.variantId ? false : true;
      }
      if (name === "quantity") {
        reqObj.qtyErr = Number(value) > 0 ? false : true;
      }
      if (name === "doseInSeries") {
        reqObj.doseErr = !value?.stepId;
      }
      return reqObj;
    });
  };

  const getVarinatList = (serId, serviceSource = null) => {
    if (serId == null || serId === "") return [];
    const src = Array.isArray(serviceSource)
      ? serviceSource
      : Array.isArray(services)
        ? services
        : [];
    const variants = src
      ?.find((ser) => ser?.serviceId === serId)
      ?.serviceVariants?.filter((vr) =>
        vr?.createdBy ? vr?.createdBy === clinicDet?.id : true,
      );
    if (!Array.isArray(variants)) return [];
    return variants.map((vnt) => ({
      name: vnt?.variantName,
      variantId: vnt?.variantId,
      servicePrice: vnt?.servicePrice ?? 0,
      value: vnt?.variantName,
      serviceCharge: vnt?.serviceCharge,
      variantNumber: vnt?.variantNumber,
      serviceId: serId,
    }));
  };

  const getDoseInSeriesList = (serId) =>
    getDoseInSeriesOptions(serviceRemainders, serId);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Add procedure"
      modal
      modalWidth={40}
      reduceHeaderSize
      hideClose
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              PROCEDURE / SERVICE
            </Typography>
            <CustomSelect
              list={getServiceList() ?? []}
              value={values?.service?.name}
              handleChange={(value) => onChange("service", value)}
              error={values?.servErr}
              helperText={values?.servErr ? reqTxt : ""}
              addNewSelect
              searchOnly
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#757575",
                mt: 1,
                lineHeight: 1.4,
              }}
            >
              Selecting a procedure with a default kit makes it available under
              "Add from procedure kit" below.
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              VARIANT
            </Typography>
            <CustomSelect
              list={getVarinatList(values?.service?.serviceId) ?? []}
              value={values?.variant?.name}
              handleChange={(e) => {
                onChange(
                  "variant",
                  getVarinatList(values?.service?.serviceId)?.find(
                    (li) => li?.name === e?.target?.value,
                  ) ?? null,
                );
              }}
              error={values?.varErr}
              helperText={values?.varErr ? "Please select a variant" : ""}
              select
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#757575",
                mt: 1,
                lineHeight: 1.4,
              }}
            >
              Selecting a procedure with a default kit makes it available under
              "Add from procedure kit" below.
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              BILLABLE QTY
            </Typography>
            <CustomTextField
              value={values?.quantity}
              handleChange={(e) => {
                onChange("quantity", e?.target?.value);
              }}
              fullWidth
              error={values?.qtyErr}
              helperText={values?.qtyErr ? reqTxt : ""}
              type="number"
            />
          </Box>

          {getDoseInSeriesList(values?.service?.serviceId).length > 0 ? (
            <Box mb={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: values?.doseErr ? "#d32f2f" : "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                {getOccurrenceLabel(
                  serviceRemainders,
                  values?.service?.serviceId,
                )}{" "}
                IN SERIES <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <CustomSelect
                list={getDoseInSeriesList(values?.service?.serviceId) ?? []}
                value={values?.doseInSeries?.name}
                handleChange={(e) => {
                  onChange(
                    "doseInSeries",
                    getDoseInSeriesList(values?.service?.serviceId)?.find(
                      (li) => li?.name === e?.target?.value,
                    ) ?? null,
                  );
                }}
                select
                error={values?.doseErr}
                helperText={values?.doseErr ? reqTxt : ""}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <CustomButton
            text="Add to procedure"
            smallBtn
            disabled={isSaving}
            onClick={handleAdd}
          />
          <Button
            onClick={handleDone}
            variant="outlined"
            disabled={isSaving}
            sx={{
              color: AppColors.appPrimary || "#0054A6",
              borderColor: AppColors.appPrimary || "#0054A6",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              height: "32px",
            }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddProcedureDrawer;
